import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import type { ChatSession } from '../chat/chat-session.js';
import type {
  ChatMessage,
  ChatSettings,
  ChatState,
  ConnectionState,
  ProgressTree,
  QueuedMessage,
  LiveLlmCall,
  BackgroundWorkItem,
} from '../chat/types.js';
import type { IndexingProgress } from '../resources/bridge-events.js';
import type { Capability } from '../types/capabilities.js';
import type { OpenQuestion } from '../types/questions.js';
import type { SessionTasks } from '../types/reasoning.js';

// Session-keyed published state. The live ChatSession handle swaps
// asynchronously (a connect() round trip), so a single global atom would
// publish the previous session's data under the new session id — the
// session-switch blip. Keying every slice by session id makes stale
// publication structurally impossible (sdk ADR-042).
export const DRAFT_SESSION_KEY = '__draft__';

export function chatSessionKeyOf(sessionId: string | null | undefined): string {
  return sessionId ?? DRAFT_SESSION_KEY;
}

export interface ChatSessionPublishedState {
  messages: ChatMessage[];
  state: ChatState;
  progressTree: ProgressTree | null;
  connectionState: ConnectionState;
  queue: QueuedMessage[];
  tasks: SessionTasks | null;
  backgroundWork: BackgroundWorkItem[];
  error: string | null;
  settings: ChatSettings | null;
  liveLlmCalls: LiveLlmCall[];
}

export function emptyDraftSessionState(): Partial<ChatSessionPublishedState> {
  return { ...emptySessionState };
}

const emptySessionState: ChatSessionPublishedState = {
  messages: [],
  state: 'idle',
  progressTree: null,
  connectionState: 'disconnected',
  queue: [],
  tasks: null,
  backgroundWork: [],
  error: null,
  settings: null,
  liveLlmCalls: [],
};

const chatSessionsStateBaseAtom = atom<Record<string, ChatSessionPublishedState>>({});

/** Write one state slice for one session. Keyed at event time, so a late
 * callback from a disconnected session can only touch its own key. */
export const applyChatSessionStateAction = atom(
  null,
  (_get, set, patch: { sessionId: string; update: Partial<ChatSessionPublishedState> }) => {
    set(chatSessionsStateBaseAtom, (prev) => {
      const current = prev[patch.sessionId] ?? emptySessionState;
      return { ...prev, [patch.sessionId]: { ...current, ...patch.update } };
    });
  }
);

export const chatSessionAtom = atom<ChatSession | null>(null);

const settingsFamily = atomFamily((key: string) =>
  atom<ChatSettings | null>((get) => get(chatSessionsStateBaseAtom)[key]?.settings ?? null)
);
const messagesFamily = atomFamily((key: string) =>
  atom<ChatMessage[]>((get) => get(chatSessionsStateBaseAtom)[key]?.messages ?? [])
);
const stateFamily = atomFamily((key: string) =>
  atom<ChatState>((get) => get(chatSessionsStateBaseAtom)[key]?.state ?? 'idle')
);
const progressTreeFamily = atomFamily((key: string) =>
  atom<ProgressTree | null>((get) => get(chatSessionsStateBaseAtom)[key]?.progressTree ?? null)
);
const queueFamily = atomFamily((key: string) =>
  atom<QueuedMessage[]>((get) => get(chatSessionsStateBaseAtom)[key]?.queue ?? [])
);
const tasksFamily = atomFamily((key: string) =>
  atom<SessionTasks | null>((get) => get(chatSessionsStateBaseAtom)[key]?.tasks ?? null)
);
const backgroundWorkFamily = atomFamily((key: string) =>
  atom<BackgroundWorkItem[]>((get) => get(chatSessionsStateBaseAtom)[key]?.backgroundWork ?? [])
);
const liveLlmCallsFamily = atomFamily((key: string) =>
  atom<LiveLlmCall[]>((get) => get(chatSessionsStateBaseAtom)[key]?.liveLlmCalls ?? [])
);

export const chatSettingsOf = (sessionId: string | null | undefined) =>
  settingsFamily(chatSessionKeyOf(sessionId));

export const chatMessagesOf = (sessionId: string | null | undefined) =>
  messagesFamily(chatSessionKeyOf(sessionId));

export const chatStateOf = (sessionId: string | null | undefined) =>
  stateFamily(chatSessionKeyOf(sessionId));

export const progressTreeOf = (sessionId: string | null | undefined) =>
  progressTreeFamily(chatSessionKeyOf(sessionId));

export const chatQueueOf = (sessionId: string | null | undefined) =>
  queueFamily(chatSessionKeyOf(sessionId));

export const chatTasksOf = (sessionId: string | null | undefined) =>
  tasksFamily(chatSessionKeyOf(sessionId));

export const chatBackgroundWorkOf = (sessionId: string | null | undefined) =>
  backgroundWorkFamily(chatSessionKeyOf(sessionId));

export const liveLlmCallsOf = (sessionId: string | null | undefined) =>
  liveLlmCallsFamily(chatSessionKeyOf(sessionId));

const openQuestionsBaseAtom = atom<OpenQuestion[]>([]);

/** The account's open questions to the user, newest first, as the live OpenQuestionsList last read them (sessions ADR-c53f5). */
export const openQuestionsAtom = atom<OpenQuestion[]>((get) => get(openQuestionsBaseAtom));

/** Publish an OpenQuestionsList's items; the host calls it from the list's onChange. */
export const setOpenQuestionsAtom = atom(null, (_get, set, next: OpenQuestion[]) => {
  set(openQuestionsBaseAtom, next);
});

/** Ids of the sessions holding an open question to the user. */
export const sessionsNeedingInputAtom = atom<ReadonlySet<string>>(
  (get) => new Set(get(openQuestionsBaseAtom).map((q) => q.session_id)),
);

export const paletteCapabilitiesVersionAtom = atom(0);

export const paletteCapabilitiesAtom = atom<Promise<Capability[]>>(async (get) => {
  get(paletteCapabilitiesVersionAtom);
  const session = get(chatSessionAtom);
  if (!session) return [];
  return session.listCapabilities();
});

export const refreshPaletteCapabilitiesAtom = atom(null, (get, set) => {
  set(paletteCapabilitiesVersionAtom, get(paletteCapabilitiesVersionAtom) + 1);
});

// --- Indexing progress (central-tier bridge fanout) atoms ---
//
// The Go search worker fans `search.indexingProgress` out to ALL of an
// account's connected devices (AccountScope), so a single frame can carry any
// workspace's progress. We key the latest frame by workspaceId; consumers
// select their own workspace's progress. The filter-by-workspace requirement
// (the indicator only shows its own workspace) is satisfied by this keying.

const indexingProgressByWorkspaceBaseAtom = atom<Record<string, IndexingProgress>>({});

export const indexingProgressByWorkspaceAtom = atom<Record<string, IndexingProgress>>((get) =>
  get(indexingProgressByWorkspaceBaseAtom)
);

function sameIndexingProgress(a: IndexingProgress | undefined, b: IndexingProgress): boolean {
  return (
    a !== undefined &&
    a.accountId === b.accountId &&
    a.workspaceId === b.workspaceId &&
    a.queued === b.queued &&
    a.embedding === b.embedding &&
    a.indexed === b.indexed &&
    a.ready === b.ready &&
    a.progress === b.progress &&
    a.health === b.health &&
    a.emitterEpoch === b.emitterEpoch &&
    a.indexRevision === b.indexRevision
  );
}

function scheduleFrame(fn: () => void): void {
  let ran = false;
  const runOnce = (): void => {
    if (ran) return;
    ran = true;
    fn();
  };
  const raf = (globalThis as { requestAnimationFrame?: (cb: () => void) => number })
    .requestAnimationFrame;
  if (raf) raf(runOnce);
  setTimeout(runOnce, 32);
}

export type IndexingCoalesceState = {
  scheduled: boolean;
  pending: Record<string, IndexingProgress>;
};

export type IndexingProgressDecision = {
  state: IndexingCoalesceState;
  immediate: IndexingProgress | null;
  scheduleDrain: boolean;
};

export function reduceIndexingProgress(
  state: IndexingCoalesceState,
  committed: Record<string, IndexingProgress>,
  progress: IndexingProgress
): IndexingProgressDecision {
  const latest = state.pending[progress.workspaceId] ?? committed[progress.workspaceId];
  if (
    (latest && progress.indexRevision <= latest.indexRevision) ||
    sameIndexingProgress(latest, progress)
  ) {
    return { state, immediate: null, scheduleDrain: false };
  }
  if (state.scheduled) {
    return {
      state: {
        scheduled: true,
        pending: { ...state.pending, [progress.workspaceId]: progress },
      },
      immediate: null,
      scheduleDrain: false,
    };
  }
  return {
    state: { scheduled: true, pending: {} },
    immediate: progress,
    scheduleDrain: true,
  };
}

export function drainIndexingProgress(state: IndexingCoalesceState): {
  state: IndexingCoalesceState;
  buffered: Record<string, IndexingProgress>;
} {
  return {
    state: { scheduled: false, pending: {} },
    buffered: state.pending,
  };
}

const indexingCoalesceAtom = atom<IndexingCoalesceState>({ scheduled: false, pending: {} });

export const applyIndexingProgressAction = atom(null, (get, set, progress: IndexingProgress) => {
  const coalesce = get(indexingCoalesceAtom);
  const decision = reduceIndexingProgress(
    coalesce,
    get(indexingProgressByWorkspaceBaseAtom),
    progress
  );
  if (decision.state !== coalesce) set(indexingCoalesceAtom, decision.state);
  const immediate = decision.immediate;
  if (immediate) {
    set(indexingProgressByWorkspaceBaseAtom, (prev) => ({
      ...prev,
      [immediate.workspaceId]: immediate,
    }));
  }
  if (!decision.scheduleDrain) return;
  scheduleFrame(() => {
    const drained = drainIndexingProgress(get(indexingCoalesceAtom));
    set(indexingCoalesceAtom, drained.state);
    const buffered = drained.buffered;
    if (Object.keys(buffered).length === 0) return;
    set(indexingProgressByWorkspaceBaseAtom, (prev) => ({ ...prev, ...buffered }));
  });
});
